import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import {
  buildCanonicalFoodFilters,
  buildCanonicalFoodSearchResults,
  FOOD_SEARCH_DEBOUNCE_MS,
  FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  getEligibleCoachFoodMatchCount,
  shouldFetchCatalogFoods,
} from "./foodSearchService";
import { normalizeFoodSearchText } from "./supabaseFoodCatalogShared";
import { fetchCatalogFoodSearchPage } from "./supabaseFoodCatalog";

function mergeUniqueFoodsById(currentFoods, nextFoods) {
  const foodsById = new Map();

  [...(Array.isArray(currentFoods) ? currentFoods : []), ...(Array.isArray(nextFoods) ? nextFoods : [])]
    .forEach((food) => {
      const foodId = String(food?.id ?? "").trim();
      if (!foodId || foodsById.has(foodId)) {
        return;
      }

      foodsById.set(foodId, food);
    });

  return Array.from(foodsById.values());
}

function areCanonicalFoodFiltersEqual(leftFilters, rightFilters) {
  if (rightFilters == null) {
    return false;
  }

  const resolvedLeftFilters = buildCanonicalFoodFilters(leftFilters);
  const resolvedRightFilters = buildCanonicalFoodFilters(rightFilters);

  return (
    resolvedLeftFilters.source === resolvedRightFilters.source &&
    resolvedLeftFilters.type === resolvedRightFilters.type
  );
}

function areEmptyQueryLibraryBrowseStatesEqual(leftState, rightState) {
  if (leftState == null || rightState == null) {
    return false;
  }

  return (
    areCanonicalFoodFiltersEqual(leftState.filters, rightState.filters) &&
    (leftState.sortField ?? null) === (rightState.sortField ?? null)
  );
}

function shouldDeferEmptyQueryPickerSortChange({
  surface = "library",
  query = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  coachFoods = [],
  pageSize = FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  requestSignature = "",
  currentSignature = "",
  requestId = 0,
  isLoading = false,
} = {}) {
  if (surface !== "picker") {
    return false;
  }

  if (normalizeFoodSearchText(query)) {
    return false;
  }

  if (sortField == null) {
    return false;
  }

  if (
    !shouldFetchCatalogFoods({
      query,
      surface,
      filters,
      coachFoods,
      pageSize,
      sortField,
    })
  ) {
    return false;
  }

  return (
    requestSignature !== currentSignature ||
    requestId === 0 ||
    isLoading === true
  );
}

function shouldDeferEmptyQueryLibraryBrowseChange({
  surface = "library",
  query = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  coachFoods = [],
  pageSize = FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  requestSignature = "",
  currentSignature = "",
  requestId = 0,
  isLoading = false,
  lastSettledEmptyQueryLibraryBrowseState = null,
} = {}) {
  if (surface !== "library") {
    return false;
  }

  if (normalizeFoodSearchText(query)) {
    return false;
  }

  if (lastSettledEmptyQueryLibraryBrowseState == null) {
    return false;
  }

  if (
    areEmptyQueryLibraryBrowseStatesEqual(
      {
        filters,
        sortField,
      },
      lastSettledEmptyQueryLibraryBrowseState,
    )
  ) {
    return false;
  }

  if (
    !shouldFetchCatalogFoods({
      query,
      surface,
      filters,
      coachFoods,
      pageSize,
      sortField,
    })
  ) {
    return false;
  }

  return (
    requestSignature !== currentSignature ||
    requestId === 0 ||
    isLoading === true
  );
}

export function shouldDeferCanonicalFoodItems({
  surface = "library",
  query = "",
  debouncedQuery = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  coachFoods = [],
  pageSize = FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  requestSignature = "",
  currentSignature = "",
  requestId = 0,
  isLoading = false,
  lastSettledEmptyQueryLibraryBrowseState = null,
} = {}) {
  const normalizedQuery = normalizeFoodSearchText(query);
  if (!normalizedQuery) {
    if (
      shouldDeferEmptyQueryPickerSortChange({
        surface,
        query,
        filters,
        sortField,
        coachFoods,
        pageSize,
        requestSignature,
        currentSignature,
        requestId,
        isLoading,
      })
    ) {
      return true;
    }

    return shouldDeferEmptyQueryLibraryBrowseChange({
      surface,
      query,
      filters,
      sortField,
      coachFoods,
      pageSize,
      requestSignature,
      currentSignature,
      requestId,
      isLoading,
      lastSettledEmptyQueryLibraryBrowseState,
    });
  }

  if (
    !shouldFetchCatalogFoods({
      query,
      surface,
      filters,
      coachFoods,
      pageSize,
      sortField,
    })
  ) {
    return false;
  }

  if (normalizeFoodSearchText(debouncedQuery) !== normalizedQuery) {
    return true;
  }

  return (
    requestSignature !== currentSignature ||
    requestId === 0 ||
    isLoading === true
  );
}

export function buildCanonicalFoodSearchSignature({
  query = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  pageSize = FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  surface = "library",
} = {}) {
  return JSON.stringify({
    query: String(query ?? "").trim(),
    filters: buildCanonicalFoodFilters(filters),
    sortField: sortField ?? null,
    pageSize: Math.max(1, Number(pageSize) || FOOD_SEARCH_DEFAULT_PAGE_SIZE),
    surface,
  });
}

export const initialCanonicalFoodSearchState = Object.freeze({
  requestId: 0,
  signature: "",
  catalogFoods: [],
  source: "idle",
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  nextCursor: null,
  error: null,
});

export const initialCanonicalFoodSearchLoadingState = Object.freeze({
  isPending: false,
  loadingSessionId: 0,
});

export function canonicalFoodSearchReducer(state, action) {
  if (action.type === "start") {
    return {
      ...state,
      requestId: action.requestId,
      signature: action.signature,
      catalogFoods: action.reset ? [] : state.catalogFoods,
      isLoading: action.reset,
      isLoadingMore: !action.reset,
      error: null,
      hasMore: action.reset ? false : state.hasMore,
      nextCursor: action.reset ? null : state.nextCursor,
    };
  }

  if (action.type === "success") {
    if (action.requestId !== state.requestId || action.signature !== state.signature) {
      return state;
    }

    return {
      ...state,
      catalogFoods: action.reset
        ? action.catalogFoods
        : mergeUniqueFoodsById(state.catalogFoods, action.catalogFoods),
      source: action.source,
      isLoading: false,
      isLoadingMore: false,
      hasMore: action.hasMore,
      nextCursor: action.nextCursor,
      error: null,
    };
  }

  if (action.type === "error") {
    if (action.requestId !== state.requestId || action.signature !== state.signature) {
      return state;
    }

    return {
      ...state,
      isLoading: false,
      isLoadingMore: false,
      hasMore: false,
      nextCursor: null,
      error: action.error,
    };
  }

  if (action.type === "reset") {
    return {
      ...initialCanonicalFoodSearchState,
      signature: action.signature,
    };
  }

  return state;
}

export function shouldTrackCanonicalFoodLoadingSession({
  query = "",
  surface = "library",
  filters = buildCanonicalFoodFilters(),
  coachFoods = [],
  pageSize = FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  isDeferringItems = false,
  isLoading = false,
  sortField = null,
  lastSettledEmptyQueryLibraryBrowseState = null,
} = {}) {
  const normalizedQuery = normalizeFoodSearchText(query);
  if (!normalizedQuery) {
    if (
      shouldDeferEmptyQueryPickerSortChange({
        surface,
        query,
        filters,
        sortField,
        coachFoods,
        pageSize,
        requestSignature: "",
        currentSignature: "",
        requestId: isDeferringItems === true || isLoading === true ? 1 : 0,
        isLoading,
      })
    ) {
      return true;
    }

    return shouldDeferEmptyQueryLibraryBrowseChange({
      surface,
      query,
      filters,
      sortField,
      coachFoods,
      pageSize,
      requestSignature: "",
      currentSignature: "",
      requestId: isDeferringItems === true || isLoading === true ? 1 : 0,
      isLoading,
      lastSettledEmptyQueryLibraryBrowseState,
    });
  }

  if (
    !shouldFetchCatalogFoods({
      query,
      surface,
      filters,
      coachFoods,
      pageSize,
      sortField,
    })
  ) {
    return false;
  }

  return isDeferringItems === true || isLoading === true;
}

export function shouldShowCanonicalFoodLoading({
  query = "",
  surface = "library",
  filters = buildCanonicalFoodFilters(),
  coachFoods = [],
  pageSize = FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  isDeferringItems = false,
  isLoading = false,
  sortField = null,
} = {}) {
  if (isDeferringItems) {
    return true;
  }

  if (!isLoading) {
    return false;
  }

  if (surface !== "picker") {
    return true;
  }

  const normalizedQuery = normalizeFoodSearchText(query);
  if (normalizedQuery) {
    return true;
  }

  if (
    !shouldFetchCatalogFoods({
      query,
      surface,
      filters,
      coachFoods,
      pageSize,
      sortField,
    })
  ) {
    return false;
  }

  return getEligibleCoachFoodMatchCount({ coachFoods, query, filters }) === 0;
}

export function canonicalFoodSearchLoadingReducer(state, action) {
  if (action.type !== "sync") {
    return state;
  }

  const isPending = action.isPending === true;
  if (isPending) {
    if (state.isPending) {
      return state;
    }

    return {
      isPending: true,
      loadingSessionId: state.loadingSessionId + 1,
    };
  }

  if (!state.isPending) {
    return state;
  }

  return {
    ...state,
    isPending: false,
  };
}

export function useCanonicalFoodSearch({
  coachFoods = [],
  query = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  pageSize = FOOD_SEARCH_DEFAULT_PAGE_SIZE,
  surface = "library",
  language = "en",
} = {}) {
  const [debouncedQuery, setDebouncedQuery] = useState(String(query ?? ""));
  const [cursor, setCursor] = useState(0);
  const [state, dispatch] = useReducer(
    canonicalFoodSearchReducer,
    initialCanonicalFoodSearchState,
  );
  const requestIdRef = useRef(0);
  const loadingStateRef = useRef(initialCanonicalFoodSearchLoadingState);
  const lastSettledEmptyQueryLibraryBrowseStateRef = useRef(null);
  const resolvedFilters = useMemo(
    () => buildCanonicalFoodFilters(filters),
    [filters],
  );
  const shouldFetchCatalog = useMemo(
    () =>
      shouldFetchCatalogFoods({
        query: debouncedQuery,
        surface,
        filters: resolvedFilters,
        coachFoods,
        pageSize,
        sortField,
      }),
    [coachFoods, debouncedQuery, pageSize, resolvedFilters, sortField, surface],
  );
  const signature = useMemo(
    () =>
      buildCanonicalFoodSearchSignature({
        query: debouncedQuery,
        filters: resolvedFilters,
        sortField,
        pageSize,
        surface,
      }),
    [debouncedQuery, pageSize, resolvedFilters, sortField, surface],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(String(query ?? ""));
    }, FOOD_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [query]);

  useEffect(() => {
    setCursor(0);
    dispatch({
      type: "reset",
      signature,
    });
  }, [signature]);

  useEffect(() => {
    if (surface !== "library") {
      return;
    }

    if (normalizeFoodSearchText(query)) {
      return;
    }

    if (
      state.requestId === 0 ||
      state.isLoading ||
      state.signature !== signature
    ) {
      return;
    }

    lastSettledEmptyQueryLibraryBrowseStateRef.current = {
      filters: resolvedFilters,
      sortField: sortField ?? null,
    };
  }, [
    query,
    resolvedFilters,
    sortField,
    signature,
    state.isLoading,
    state.requestId,
    state.signature,
    surface,
  ]);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const reset = cursor === 0;

    dispatch({
      type: "start",
      requestId,
      signature,
      reset,
    });

    if (!shouldFetchCatalog) {
      dispatch({
        type: "success",
        requestId,
        signature,
        reset,
        catalogFoods: [],
        source: "local",
        hasMore: false,
        nextCursor: null,
      });
      return undefined;
    }

    fetchCatalogFoodSearchPage({
      query: debouncedQuery,
      filters: resolvedFilters,
      sortField,
      cursor,
      pageSize,
    })
      .then((result) => {
        dispatch({
          type: "success",
          requestId,
          signature,
          reset,
          catalogFoods: result?.foods ?? [],
          source: result?.source ?? "unknown",
          hasMore: result?.hasMore === true,
          nextCursor: result?.nextCursor ?? null,
        });
      })
      .catch((error) => {
        dispatch({
          type: "error",
          requestId,
          signature,
          error,
        });
      });
  }, [
    cursor,
    debouncedQuery,
    pageSize,
    resolvedFilters,
    shouldFetchCatalog,
    signature,
    sortField,
    surface,
  ]);

  const shouldDeferItems = useMemo(
    () =>
      shouldDeferCanonicalFoodItems({
        surface,
        query,
        debouncedQuery,
        filters: resolvedFilters,
        coachFoods,
        pageSize,
        requestSignature: state.signature,
        currentSignature: signature,
        requestId: state.requestId,
        isLoading: state.isLoading,
        sortField,
        lastSettledEmptyQueryLibraryBrowseState:
          lastSettledEmptyQueryLibraryBrowseStateRef.current,
      }),
    [
      query,
      debouncedQuery,
      coachFoods,
      pageSize,
      resolvedFilters,
      signature,
      state.isLoading,
      state.requestId,
      state.signature,
      sortField,
      surface,
    ],
  );
  const shouldTrackLoadingSession = useMemo(
    () =>
      shouldTrackCanonicalFoodLoadingSession({
        query,
        surface,
        filters: resolvedFilters,
        coachFoods,
        pageSize,
        isDeferringItems: shouldDeferItems,
        isLoading: state.isLoading,
        sortField,
        lastSettledEmptyQueryLibraryBrowseState:
          lastSettledEmptyQueryLibraryBrowseStateRef.current,
      }),
    [
      coachFoods,
      pageSize,
      query,
      resolvedFilters,
      shouldDeferItems,
      state.isLoading,
      sortField,
      surface,
    ],
  );
  const loadingState = useMemo(() => {
    const nextLoadingState = canonicalFoodSearchLoadingReducer(
      loadingStateRef.current,
      {
        type: "sync",
        isPending: shouldTrackLoadingSession,
      },
    );

    loadingStateRef.current = nextLoadingState;
    return nextLoadingState;
  }, [shouldTrackLoadingSession]);

  const items = useMemo(
    () => {
      if (shouldDeferItems) {
        return [];
      }

      return buildCanonicalFoodSearchResults({
        coachFoods,
        catalogFoods: state.catalogFoods,
        query: debouncedQuery,
        filters: resolvedFilters,
        sortField,
        language,
      });
    },
    [
      coachFoods,
      debouncedQuery,
      language,
      resolvedFilters,
      shouldDeferItems,
      sortField,
      state.catalogFoods,
    ],
  );
  const isLoading = useMemo(
    () =>
      shouldShowCanonicalFoodLoading({
        query,
        surface,
        filters: resolvedFilters,
        coachFoods,
        pageSize,
        isDeferringItems: shouldDeferItems,
        isLoading: state.isLoading,
        sortField,
      }),
    [
      coachFoods,
      pageSize,
      query,
      resolvedFilters,
      shouldDeferItems,
      state.isLoading,
      sortField,
      surface,
    ],
  );
  const loadMore = useCallback(() => {
    if (!state.hasMore || state.isLoadingMore || state.nextCursor == null) {
      return;
    }

    setCursor(state.nextCursor);
  }, [state.hasMore, state.isLoadingMore, state.nextCursor]);

  return {
    items,
    query: debouncedQuery,
    isLoading,
    isDeferringItems: shouldDeferItems,
    loadingSessionId: loadingState.isPending ? loadingState.loadingSessionId : null,
    isLoadingMore: state.isLoadingMore,
    hasMore: state.hasMore,
    nextCursor: state.nextCursor,
    error: state.error,
    sourceState: {
      catalogSource: state.source,
    },
    loadMore,
  };
}
